import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  FormControlLabel,
  Switch,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { capitalCase } from 'change-case';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';
import Page from 'src/components/Page';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { WorkflowManagementService } from './api/WorkflowManagementService';

interface Checklist {
  id: string;
  code: string;
  name: string;
  description: string;
  status: string;
}

interface ChecklistItem {
  id: string;
  code: string;
  name: string;
  description: string;
  conditions: string;
  metadata: string;
  data_schema: string;
  isRequired: boolean;
}

interface DetailItemProps {
  label: string;
  value?: string | null;
}

const DetailItem = ({ label, value }: DetailItemProps) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
      {label}
    </Typography>
    <Typography variant="body2">{value || 'N/A'}</Typography>
  </Grid>
);

const checklistItemInitialState: ChecklistItem = {
  id: '',
  code: '',
  name: '',
  description: '',
  conditions: '',
  metadata: '',
  data_schema: '',
  isRequired: false,
};

export default function ChecklistDetails() {
  const { code } = useParams<{ code: string }>();
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [openItemDialog, setOpenItemDialog] = useState(false);
  const [itemInForm, setItemInForm] = useState<ChecklistItem>(checklistItemInitialState);
  const [editingItem, setEditingItem] = useState<ChecklistItem | null>(null);

  const fetchChecklistData = useCallback(async () => {
    if (code) {
      try {
        setLoading(true);
        const checklistResponse = await WorkflowManagementService.getChecklist(code);
        setChecklist(checklistResponse);
        if (checklistResponse && checklistResponse.id) {
          const itemsResponse = await WorkflowManagementService.getItems(code);
          // The order property is inside conditions json
          const getOrderFromConditions = (item: any) => { try { return JSON.parse(item.conditions).order || 0 } catch { return 0 } };
          if (Array.isArray(itemsResponse)) { 
            itemsResponse.sort((a, b) => getOrderFromConditions(a) - getOrderFromConditions(b));
            itemsResponse.sort((a, b) => a.order - b.order);
          }
          setItems(itemsResponse);
        }
      } catch (error) {
        console.error('Failed to fetch checklist data', error);
      } finally {
        setLoading(false);
      }
    }
  }, [code]);

  useEffect(() => {
    fetchChecklistData();
  }, [fetchChecklistData]);

  const handleOpenItemDialog = (item?: ChecklistItem) => {
    if (item) {
      setEditingItem(item);
      setItemInForm(item);
    } else {
      setEditingItem(null);
      setItemInForm({ ...checklistItemInitialState });
    }
    setOpenItemDialog(true);
  };

  const handleCloseItemDialog = () => {
    setOpenItemDialog(false);
    setItemInForm(checklistItemInitialState);
    setEditingItem(null);
  };

  const handleItemInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setItemInForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSaveItem = async () => {
    if (!checklist) return;
    try {
      if (editingItem) {
        await WorkflowManagementService.updateItem(checklist.code, editingItem.id, itemInForm);
      } else {
        await WorkflowManagementService.createItem(checklist.code, itemInForm);
      }
      handleCloseItemDialog();
      fetchChecklistData();
    } catch (error) {
      console.error('Failed to save checklist item', error);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!checklist || !window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await WorkflowManagementService.deleteItem(checklist.id, itemId);
      fetchChecklistData();
    } catch (error) {
      console.error('Failed to delete checklist item', error);
    }
  };

  return (
    <Page title="Checklist Details">
      <Container>
        <HeaderBreadcrumbs
          heading=""
          title="Checklist Details"
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Checklists', href: PATH_DASHBOARD.crs.checklist },
            { name: checklist?.name || 'Details' },
          ]}
        />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && checklist && (
          <>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ flexGrow: 1 }}>
                    {checklist.name}
                  </Typography>
                  <Chip
                    label={capitalCase(checklist.status || 'N/A')}
                    color={checklist.status === 'active' ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                <Grid container spacing={2}>
                  <DetailItem label="Code" value={checklist.code} />
                  <DetailItem label="Description" value={checklist.description} />
                </Grid>
              </CardContent>
            </Card>

            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5">Items</Typography>
                  <Button variant="contained" startIcon={<Iconify icon={'eva:plus-fill'} />} onClick={() => handleOpenItemDialog()}>
                    New Item
                  </Button>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Is Required?</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Code</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.isRequired?"Yes":"No"}</TableCell>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>
                            <Link component="button" variant="body2" onClick={() => handleOpenItemDialog(item)}>
                              {item.code}
                            </Link>
                          </TableCell>
                          <TableCell>{item.description}</TableCell>
                          <TableCell align="right">
                            <Button size="small" color="error" onClick={() => handleDeleteItem(item.id)}>
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </>
        )}

        <Dialog open={openItemDialog} onClose={handleCloseItemDialog} fullWidth maxWidth="sm">
          <DialogTitle>{editingItem ? 'Edit Item' : 'New Item'}</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <TextField name="code" label="Code" value={itemInForm.code} onChange={handleItemInputChange} fullWidth disabled={!!editingItem} />
              <TextField name="name" label="Name" value={itemInForm.name} onChange={handleItemInputChange} fullWidth />
              <TextField name="description" label="Description" value={itemInForm.description} onChange={handleItemInputChange} fullWidth multiline rows={3} />
              <TextField name="conditions" label="Conditions (JSON)" value={itemInForm.conditions} onChange={handleItemInputChange} fullWidth multiline rows={3} />
              <TextField name="metadata" label="Metadata (JSON)" value={itemInForm.metadata} onChange={handleItemInputChange} fullWidth multiline rows={3} />
              <TextField name="data_schema" label="Data Schema (JSON)" value={itemInForm.data_schema} onChange={handleItemInputChange} fullWidth multiline rows={3} />
              <FormControlLabel control={<Switch checked={itemInForm.isRequired} onChange={handleItemInputChange} name="isRequired" />} label="Is Required" />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseItemDialog}>Cancel</Button>
            <Button onClick={handleSaveItem} variant="contained">{editingItem ? 'Save' : 'Create'}</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}